import { FC, FormEvent, ReactNode, useState } from 'react'
import { Button } from '../components/ui/Button'
import { ButtonLink } from '../components/ui/ButtonLink'
import { Panel } from '../components/ui/Panel'
import { PageHeader } from '../components/ui/PageHeader'
import { eventsData } from '../data'
import burningTheDream from 'url:../assets/bands/burning-the-dream.webp'
import centrilia from 'url:../assets/bands/centrilia.webp'
import jorogumo from 'url:../assets/bands/jorogumo-3.jpg'
import permacrisisVideo from 'url:../assets/bands/permacrisis.mp4'
import cnocAnTursa from 'url:../assets/bands/2026/cnoc-an-tursa.jpg'
import chekhovsGun from 'url:../assets/bands/2026/chekhovs-gun.jpg'
import pict from 'url:../assets/bands/2026/pict.jpg'
import arcArrival from 'url:../assets/bands/2026/arc-arrival.jpg'
import armouredCombat from 'url:../assets/misc/2026/medieval-armoured-combat-show.png'

const NOTIFY_ENDPOINT = 'https://formspree.io/f/xojbdkzg'

const MONTHS: Record<string, number> = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
}

export const parseEventDate = (dateStr: string): Date | null => {
  const match = dateStr.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/)
  if (!match) return null
  const month = MONTHS[match[2]]
  if (month === undefined) return null
  return new Date(parseInt(match[3]), month, parseInt(match[1]))
}

export const isEventPast = (dateStr: string): boolean => {
  const eventDate = parseEventDate(dateStr)
  if (!eventDate) return false
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 1)
  cutoff.setHours(0, 0, 0, 0)
  return eventDate < cutoff
}

const isVideoUrl = (value: string) => value.split('?')[0].toLowerCase().endsWith('.mp4')

const bandDetails: Record<
  string,
  {
    name: string
    description: (isPast: boolean) => ReactNode
    imageUrl: string
    label?: string
    /** CSS object-position for the upcoming-event card crop (defaults to top). */
    objectPosition?: string
  }[]
> = {
  [eventsData.events[0]?.ticketLink ?? '']: [
    {
      name: 'Burning The Dream',
      description: (isPast) => (
        <>
          <p>🏴🇺🇦 Burning The Dream will play Slava Ukraini 🏴🇺🇦</p>
          <p className='mt-3'>
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/burningthedream'
              rel='noreferrer'
              target='_blank'
            >
              @burningthedream
            </a>{' '}
            is a progressive metal band known for blending death, thrash, metalcore, and nu-metal
            influences. They formed around 2014 and have plenty of gigging experience under their
            belt.
          </p>
          <p className='mt-3'>
            In 2018 they released their Debut EP &ldquo;Dark Waters&rdquo;. Afterwards, they
            released the incredible single &ldquo;Name Yourself&rdquo; in 2024 and
            &ldquo;Rebirth&rdquo; in 2025. Come and see them live on Saturday the 21st of February
            at The Classic Grand, Glasgow. Be prepared to be crushed by their immense Riffage!!
          </p>
          {!isPast && (
            <p className='mt-3'>
              Tickets:{' '}
              <ButtonLink
                href={eventsData.events[0]?.ticketLink ?? '#'}
                rel='noreferrer'
                target='_blank'
                variant='secondary'
                size='sm'
              >
                Tickets Scotland
              </ButtonLink>
            </p>
          )}
        </>
      ),
      imageUrl: burningTheDream,
    },
    {
      name: 'Centrilia',
      description: (isPast) => (
        <>
          <p>🏴🇺🇦 Centrilia at Slava Ukraini - Saturday 21st of February 🏴🇺🇦</p>
          <p className='mt-3'>
            They are a melodic death metal/metalcore band formed in 2013. Known for their powerful
            blend of groove intensity and clean precision. They draw inspiration from Pantera,
            Machine Head and Bleed From Within.
          </p>
          <p className='mt-3'>
            Their debut album &ldquo;In The Name of Nothing&rdquo; was mixed by legendary producer
            Terry Date (producer for Pantera, Deftones and Slayer).{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/centrilia_'
              rel='noreferrer'
              target='_blank'
            >
              @centrilia_
            </a>{' '}
            have since released blistering singles like: &ldquo;In The Blood&rdquo; (feat. Scott
            Kennedy from Blled From Within) and &ldquo;Out Come Knoves&rdquo; - a fierce protest
            anthem.
          </p>
          <p className='mt-3'>
            Come and hear them live on Saturday the 21st of February at{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/theclassicgrand'
              rel='noreferrer'
              target='_blank'
            >
              @theclassicgrand
            </a>
            , Glasgow. Be prepared to be hit by their riffs and breakdowns!
          </p>
          {!isPast && (
            <p className='mt-3'>
              Tickets:{' '}
              <ButtonLink
                href={eventsData.events[0]?.ticketLink ?? '#'}
                rel='noreferrer'
                target='_blank'
                variant='secondary'
                size='sm'
              >
                Tickets Scotland
              </ButtonLink>
            </p>
          )}
        </>
      ),
      imageUrl: centrilia,
    },
    {
      name: 'Jorogumo',
      description: (isPast) => (
        <>
          <p>🏴🇺🇦 Jorogumo will play Slava Ukraini - 4 years of war memorial 🏴🇺🇦</p>
          <p className='mt-3'>
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/jorogumogla'
              rel='noreferrer'
              target='_blank'
            >
              @jorogumogla
            </a>{' '}
            are a Sludge/Death metal band that self describes as &ldquo;Heavy music for deprived
            individuals&rdquo;. Their band name comes from Japanese folklore; Jorogumo is a spider
            creature that can shapeshift into a woman. Their recent album &ldquo;Babylon&rdquo; was
            released in October 2025, and it already has great reviews! They are also part of the
            m2tm Bloodstock Festival battle organised by{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/slowdragonmusic'
              rel='noreferrer'
              target='_blank'
            >
              @slowdragonmusic
            </a>
            . Check them out!
          </p>
          <p className='mt-3'>
            Come and hear them live on Saturday, the 21st of February at{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/theclassicgrand'
              rel='noreferrer'
              target='_blank'
            >
              @theclassicgrand
            </a>
            , Glasgow!
          </p>
          {!isPast && (
            <p className='mt-3'>
              Tickets:{' '}
              <ButtonLink
                href={eventsData.events[0]?.ticketLink ?? '#'}
                rel='noreferrer'
                target='_blank'
                variant='secondary'
                size='sm'
              >
                Tickets Scotland
              </ButtonLink>
            </p>
          )}
        </>
      ),
      imageUrl: jorogumo,
    },
    {
      name: 'Permacrisis',
      description: (isPast) => (
        <>
          <p>Permacrisis release 🔥🔥</p>
          <p className='mt-3'>Young. Uncut. Fast. Go and stream their new single now!</p>
          <p className='mt-3'>
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/permacrisisband'
              rel='noreferrer'
              target='_blank'
            >
              @permacrisisband
            </a>{' '}
            will be performing at our next event on Saturday the 21st of February in{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/theclassicgrand'
              rel='noreferrer'
              target='_blank'
            >
              @theclassicgrand
            </a>
            ! They are a Scottish thrash metal band formed in 2023 in Kirkaldy. Known for their
            explosive performance, heavy riffs and strong vocals. They even got a nomination for the{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/scotliveawards'
              rel='noreferrer'
              target='_blank'
            >
              @scotliveawards
            </a>{' '}
            (Rock and metal act of the year) and are part of the{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/m2tmofficial'
              rel='noreferrer'
              target='_blank'
            >
              @m2tmofficial
            </a>{' '}
            battle to play{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/bloodstockopenair'
              rel='noreferrer'
              target='_blank'
            >
              @bloodstockopenair
            </a>{' '}
            Festival. Come and see them now as they are conquering the scene!
          </p>
          {!isPast && (
            <p className='mt-3'>
              Tickets:{' '}
              <ButtonLink
                href={eventsData.events[0]?.ticketLink ?? '#'}
                rel='noreferrer'
                target='_blank'
                variant='secondary'
                size='sm'
              >
                Tickets Scotland
              </ButtonLink>
            </p>
          )}
        </>
      ),
      imageUrl: permacrisisVideo,
    },
  ],
  [eventsData.events[1]?.ticketLink ?? '']: [
    {
      name: 'Cnoc An Tursa',
      description: () => (
        <>
          <p>
            🏴🇺🇦{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/cnocantursaband'
              rel='noreferrer'
              target='_blank'
            >
              @cnocantursaband
            </a>
          </p>
        </>
      ),
      imageUrl: cnocAnTursa,
      objectPosition: 'center 65%',
    },
    {
      name: "Chekhov's Gun",
      description: () => (
        <>
          <p>
            🏴🇺🇦{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/chekhovsgun.official'
              rel='noreferrer'
              target='_blank'
            >
              @chekhovsgun.official
            </a>
          </p>
        </>
      ),
      imageUrl: chekhovsGun,
    },
    {
      name: 'Pict',
      description: () => (
        <>
          <p>
            🏴🇺🇦{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/pictmetal'
              rel='noreferrer'
              target='_blank'
            >
              @pictmetal
            </a>
          </p>
        </>
      ),
      imageUrl: pict,
    },
    {
      name: 'Arc Arrival',
      description: () => (
        <>
          <p>
            🏴🇺🇦{' '}
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/arc_arrival_official'
              rel='noreferrer'
              target='_blank'
            >
              @arc_arrival_official
            </a>{' '}
            is a metalcore band from Central Scotland.
          </p>
        </>
      ),
      imageUrl: arcArrival,
      objectPosition: '76% center',
    },
    {
      name: 'Edinburgh Armoured Combat Team',
      label: 'Show',
      description: () => (
        <>
          <p>
            ⚔️ Edinburgh Armoured Combat Team will join Slava Ukraini to perform a medieval armoured
            combat show ⚔️
          </p>
          <p className='mt-3'>
            <a
              className='underline decoration-brand-blue/40 hover:text-brand-blue'
              href='https://www.instagram.com/edinburgh_armoured_combat'
              rel='noreferrer'
              target='_blank'
            >
              @edinburgh_armoured_combat
            </a>
          </p>
        </>
      ),
      imageUrl: armouredCombat,
    },
  ],
}

const NotifyForm: FC = () => {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    const data = new FormData(e.currentTarget)
    const res = await fetch(NOTIFY_ENDPOINT, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data,
    })
    setSubmitting(false)
    if (res.ok) {
      setSubmitted(true)
    } else {
      setError('Something went wrong. Please try again.')
    }
  }

  return (
    <Panel className='bg-brand-smoky/80'>
      {submitted ? (
        <div className='text-sm text-brand-azure/80'>
          <p className='font-semibold text-brand-blue'>You&apos;re on the list!</p>
          <p className='mt-1'>We&apos;ll let you know as soon as a new event is announced.</p>
        </div>
      ) : (
        <div>
          <p className='text-sm font-semibold text-brand-blue'>Get notified about new events</p>
          <p className='mt-1 text-sm text-brand-azure/70'>
            Leave your email and we&apos;ll reach out when we announce the next show.
          </p>
          <form onSubmit={handleSubmit} className='mt-4 flex flex-wrap gap-3'>
            <input
              className='min-w-0 flex-1 rounded-xl bg-brand-azure/15 px-4 py-2 text-sm text-brand-azure ring-1 ring-black/50 shadow-[inset_0_1px_6px_rgba(0,0,0,0.45)] placeholder:text-brand-azure/40'
              name='email'
              required
              type='email'
              autoComplete='email'
              placeholder='your@email.com'
            />
            {error && <p className='w-full text-sm text-red-400'>{error}</p>}
            <Button variant='primary' size='sm' type='submit' disabled={submitting}>
              {submitting ? 'Sending…' : 'Notify me'}
            </Button>
          </form>
        </div>
      )}
    </Panel>
  )
}

/** Band photo, or looping video for video assets. */
const BandMedia: FC<{
  band: { name: string; imageUrl: string }
  className?: string
  objectPosition?: string
}> = ({ band, className = '', objectPosition }) =>
  isVideoUrl(band.imageUrl) ? (
    <video
      className={`h-full w-full object-cover ${className}`}
      style={{ objectPosition }}
      autoPlay
      muted
      loop
      playsInline
    >
      <source src={band.imageUrl} type='video/mp4' />
    </video>
  ) : (
    <img
      src={band.imageUrl}
      alt={band.name}
      className={`h-full w-full object-cover ${className}`}
      style={{ objectPosition }}
    />
  )

const EventCard: FC<{ event: (typeof eventsData.events)[number]; isPast: boolean }> = ({
  event,
  isPast,
}) => (
  <Panel className='bg-brand-smoky/80' as='article' ariaLabel={event.title}>
    <div className='flex flex-wrap items-start justify-between gap-4'>
      <div>
        <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>{event.title}</p>
        <h2 className='mt-2 text-2xl font-semibold text-brand-blue'>{event.date}</h2>
        <p className='mt-2 text-brand-azure/80 md:text-lg'>{event.time}</p>
      </div>
      <span className='rounded-lg px-4 py-1 text-sm uppercase tracking-[0.3em] text-brand-azure/80 ring-1 ring-black/20 bg-brand-smoky/60'>
        {event.locationTag}
      </span>
    </div>

    <div className='mt-4 grid gap-3 text-brand-azure/80 md:text-lg'>
      <p>
        <span className='text-brand-blue'>Bands:</span> {event.bands}
      </p>
      <p>
        <span className='text-brand-blue'>Venue:</span>{' '}
        <a
          className='underline decoration-brand-blue/40 hover:text-brand-blue'
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`}
          rel='noreferrer'
          target='_blank'
        >
          {event.venue}
        </a>
      </p>
      <p>
        <span className='text-brand-blue'>Tickets:</span> {event.tickets}
      </p>
      <p>
        <span className='text-brand-blue'>Age:</span> {event.agePolicy}
      </p>
      {!isPast && event.ticketLink.startsWith('http') && (
        <p>
          <span className='text-brand-blue'>Buy tickets:</span>{' '}
          <ButtonLink
            href={event.ticketLink}
            rel='noreferrer'
            target='_blank'
            variant='primary'
            size='sm'
          >
            Tickets Scotland
          </ButtonLink>
        </p>
      )}
    </div>

    {isPast ? (
      <div className='mt-8 grid gap-6'>
        {(bandDetails[event.ticketLink] ?? []).map((band) => (
          <div
            key={band.name}
            className='grid gap-4 rounded-2xl bg-brand-smoky/90 p-5 shadow-[0_16px_36px_rgba(0,0,0,0.35)] ring-1 ring-black/20 md:grid-cols-[1.1fr_1.4fr] md:items-stretch'
          >
            <div
              className={`overflow-hidden rounded-xl bg-brand-smoky ring-1 ring-black/20 ${
                isVideoUrl(band.imageUrl) ? 'aspect-[1/1.414] w-full' : 'md:h-full md:min-h-[260px]'
              }`}
            >
              <BandMedia band={band} />
            </div>
            <div className='md:flex md:flex-col md:justify-center'>
              <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>
                {band.label ?? 'Band'}
              </p>
              <h3 className='mt-2 text-xl font-semibold text-brand-blue'>{band.name}</h3>
              <div className='mt-3 text-brand-azure/80 md:text-lg'>{band.description(isPast)}</div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className='mt-8 grid gap-6 md:grid-cols-2'>
        {(bandDetails[event.ticketLink] ?? []).map((band) => (
          <div
            key={band.name}
            className={[
              'relative aspect-square overflow-hidden rounded-2xl bg-brand-smoky shadow-[0_16px_36px_rgba(0,0,0,0.35)] ring-1 ring-black/20',
              // Centre a lone card on the last row
              'md:last:odd:col-span-2 md:last:odd:w-[calc(50%-0.75rem)] md:last:odd:justify-self-center',
            ].join(' ')}
          >
            <BandMedia band={band} className='object-top' objectPosition={band.objectPosition} />
            <div className='absolute inset-x-0 bottom-0 p-5 pt-24'
              style={{
                background:
                  'linear-gradient(to top, var(--color-black-primary) 0%, color-mix(in srgb, var(--color-black-primary) 85%, transparent) 55%, transparent 100%)',
              }}>
              <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>
                {band.label ?? 'Band'}
              </p>
              <h3 className='mt-2 text-xl font-semibold text-brand-blue'>{band.name}</h3>
              <div className='mt-3 text-brand-azure/90 md:text-lg'>{band.description(isPast)}</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </Panel>
)

/** Events listing page for upcoming and past fundraisers. */
const Events: FC = () => {
  const upcomingEvents = eventsData.events.filter((e) => !isEventPast(e.date))
  const pastEvents = eventsData.events.filter((e) => isEventPast(e.date))

  return (
    <div className='space-y-6'>
      <PageHeader title='Events' subtitle='Upcoming performances and fundraiser dates.' />

      <section className='space-y-6'>
        <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>Upcoming</p>

        {upcomingEvents.length > 0 && (
          <div className='grid gap-6'>
            {upcomingEvents.map((event) => (
              <EventCard key={event.ticketLink} event={event} isPast={false} />
            ))}
          </div>
        )}

        <NotifyForm />
      </section>

      {pastEvents.length > 0 && (
        <section className='space-y-6'>
          <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>Past</p>
          <div className='grid gap-6'>
            {pastEvents.map((event) => (
              <EventCard key={event.ticketLink} event={event} isPast={true} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export default Events
